import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import axios from 'axios';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FALLBACK_LOGO = "/logo%20viajes.png";

// CSS filter to remove white background from logo
const LOGO_STYLE = {
  filter: 'drop-shadow(0 0 0 transparent)',
  mixBlendMode: 'multiply'
};

const tripCategories = [
  { name: "Viajes de Novios", slug: "novios" },
  { name: "Viajes de Grupos", slug: "grupos" },
  { name: "Cruceros", slug: "cruceros" },
  { name: "Bienestar y Relax", slug: "bienestar" },
  { name: "Arte e Historia", slug: "arte" },
  { name: "Lujo y Compras", slug: "lujo" },
  { name: "Deportivo y Cultural", slug: "deportivo" },
  { name: "Enológicos y Gastronómicos", slug: "enologico" },
  { name: "Naturaleza y Aventura", slug: "naturaleza" },
  { name: "Viajes a Medida", slug: "medida" },
];

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState(FALLBACK_LOGO);
  const [destinations, setDestinations] = useState([]);
  const [navbarColors, setNavbarColors] = useState({
    transparent: '#ffffff',
    scroll: '#1e3a8a'
  });
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const loadLogo = async () => {
      try {
        const res = await axios.get(`${API}/media`);
        const activeLogo = res.data.find(m => m.category === 'logo' && m.is_active);
        if (activeLogo) {
          setLogoUrl(`${process.env.REACT_APP_BACKEND_URL}${activeLogo.url}`);
        }
      } catch (err) {
        console.error('Error loading logo:', err);
      }
    };
    loadLogo();
  }, []);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings`);
        setNavbarColors({
          transparent: res.data.navbar_transparent_color || '#ffffff',
          scroll: res.data.navbar_scroll_color || '#1e3a8a'
        });
      } catch (err) {
        console.error('Error loading settings:', err);
      }
    };
    loadSettings();
  }, []);

  useEffect(() => {
    const fetchDestinations = async () => {
      try {
        const res = await axios.get(`${API}/destinations`);
        setDestinations(res.data);
      } catch (err) {
        console.error('Error fetching destinations:', err);
      }
    };
    fetchDestinations();
  }, []);

  const isActive = (path) => location.pathname === path;
  const isHome = location.pathname === '/';
  
  // Determinar color del texto según scroll y página
  const textColor = (isScrolled || !isHome) ? navbarColors.scroll : navbarColors.transparent;

  return (
    <>
      <nav
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled || !isHome
            ? 'glass shadow-lg py-3'
            : 'bg-gradient-to-b from-black/40 to-transparent py-6'
        }`}
      >
        <div className="container-custom">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" data-testid="logo-link" className="flex items-center shrink-0">
              <img
                src={logoUrl}
                alt="Viajes de Ensueño"
                className="h-14 md:h-16 w-auto shrink-0"
                style={{ 
                  mixBlendMode: isScrolled || !isHome ? 'normal' : 'multiply',
                  filter: isScrolled || !isHome ? 'none' : 'brightness(1.1)'
                }}
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden xl:flex items-center gap-4 2xl:gap-6 shrink-0 ml-6 2xl:ml-10">
              <Link
                to="/"
                data-testid="nav-home"
                className={`nav-link ${isActive('/') ? 'active' : ''}`}
                style={{ color: textColor }}
              >
                Home
              </Link>

              <DropdownMenu>
                <DropdownMenuTrigger
                  data-testid="nav-tipos-viajes"
                  className="nav-link flex items-center gap-1 outline-none"
                  style={{ color: textColor }}
                >
                  Tipos de Viajes
                  <ChevronDown className="w-4 h-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 bg-white shadow-xl border-0 rounded-xl p-2">
                  {tripCategories.map((cat) => (
                    <DropdownMenuItem key={cat.slug} asChild>
                      <Link
                        to={`/tipos-viajes/${cat.slug}`}
                        data-testid={`nav-category-${cat.slug}`}
                        className="px-4 py-2 cursor-pointer rounded-lg hover:bg-secondary/10 transition-colors"
                      >
                        {cat.name}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger
                  className="nav-link flex items-center gap-1 outline-none"
                  style={{ color: textColor }}
                >
                  Destinos
                  <ChevronDown className="w-4 h-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-64 bg-white shadow-xl border-0 rounded-xl p-2 max-h-[70vh] overflow-y-auto">
                  {destinations && destinations.length > 0 ? (
                    Object.entries(
                      destinations.reduce((acc, dest) => {
                        const continent = dest.continent || "Otros";
                        if (!acc[continent]) acc[continent] = [];
                        acc[continent].push(dest);
                        return acc;
                      }, {})
                    ).map(([continent, items]) => (
                      <DropdownMenuSub key={continent}>
                        <DropdownMenuSubTrigger 
                          className="px-4 py-2.5 cursor-pointer rounded-lg hover:bg-secondary/10 transition-colors flex items-center gap-2 text-sm font-semibold text-slate-700 data-[state=open]:bg-secondary/10 outline-none"
                        >
                          <span className="w-2 h-2 rounded-full bg-accent/50 shrink-0" />
                          {continent}
                        </DropdownMenuSubTrigger>
                        <DropdownMenuPortal>
                          <DropdownMenuSubContent className="w-56 bg-white shadow-2xl border-0 rounded-xl p-2 ml-1 animate-in slide-in-from-left-2">
                            {items.map((dest) => (
                              <DropdownMenuItem key={dest.slug} asChild>
                                <Link
                                  to={`/destinos/${dest.slug}`}
                                  className="px-4 py-2 cursor-pointer rounded-lg hover:bg-secondary/10 transition-colors block text-sm focus:bg-secondary/10 outline-none"
                                >
                                  {dest.name}
                                </Link>
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuSubContent>
                        </DropdownMenuPortal>
                      </DropdownMenuSub>
                    ))
                  ) : (
                    <div className="px-4 py-2 text-sm text-muted-foreground animate-pulse">
                      Cargando destinos...
                    </div>
                  )}
                  
                  <div className="h-px bg-slate-100 my-2" />
                  <DropdownMenuItem asChild>
                    <Link
                      to="/destinos"
                      className="px-4 py-2 cursor-pointer rounded-lg hover:bg-accent/10 text-accent font-semibold transition-colors italic flex items-center justify-between"
                    >
                      Ver todos los destinos
                      <ChevronDown className="w-3 h-3 -rotate-90" />
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Link
                to="/viajes"
                data-testid="nav-coleccion-viajes"
                className={`nav-link ${isActive('/viajes') ? 'active' : ''}`}
                style={{ color: textColor }}
              >
                Colección de Viajes
              </Link>

              <Link
                to="/guias-personalizadas"
                data-testid="nav-guias"
                className={`nav-link ${isActive('/guias-personalizadas') ? 'active' : ''}`}
                style={{ color: textColor }}
              >
                Guías Personalizadas
              </Link>

              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to="/servicios-empresas"
                      className={`nav-link ${isActive('/servicios-empresas') ? 'active' : ''}`}
                      style={{ color: textColor }}
                    >
                      Colectivos
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent className="bg-primary text-white border-none py-2 px-4 shadow-xl rounded-lg">
                    <p className="text-xs font-bold uppercase tracking-widest">Empresas y Colectivos</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <Link
                to="/sobre-nosotros"
                data-testid="nav-nosotros"
                className={`nav-link ${isActive('/sobre-nosotros') ? 'active' : ''}`}
                style={{ color: textColor }}
              >
                Sobre Nosotros
              </Link>

              <Link
                to="/blog"
                data-testid="nav-blog"
                className={`nav-link ${isActive('/blog') ? 'active' : ''}`}
                style={{ color: textColor }}
              >
                Blog
              </Link>
            </div>

            {/* CTA Button */}
            <div className="hidden xl:block shrink-0 ml-6 2xl:ml-10">
              <Link
                to="/contacto"
                data-testid="nav-cta"
                className="btn-accent text-sm px-4 2xl:px-6 py-2.5 2xl:py-3 whitespace-nowrap"
              >
                Planifica tu Viaje
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-button"
              onClick={() => setIsMobileMenuOpen(true)}
              className="xl:hidden p-2"
            >
              <Menu className="w-6 h-6 text-primary" />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="mobile-menu overflow-y-auto max-h-screen pb-20" data-testid="mobile-menu">
          <div className="flex justify-between items-center mb-8">
            <img src={logoUrl} alt="Viajes de Ensueño" className="h-12" />
            <button
              data-testid="mobile-menu-close"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <X className="w-6 h-6 text-primary" />
            </button>
          </div>

          <div className="flex flex-col gap-2">
            <Link
              to="/"
              onClick={() => setIsMobileMenuOpen(false)}
              className="mobile-menu-link"
            >
              Home
            </Link>

            <div className="py-4 border-b border-slate-100">
              <span className="text-xl font-serif text-primary mb-2 block">
                Tipos de Viajes
              </span>
              <div className="grid grid-cols-2 gap-2 mt-3">
                {tripCategories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/tipos-viajes/${cat.slug}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="text-sm text-muted-foreground hover:text-primary py-1"
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>

            <div className="py-4 border-b border-slate-100">
              <span className="text-xl font-serif text-primary mb-2 block">
                Destinos
              </span>
              <div className="space-y-6 mt-4">
                {destinations && destinations.length > 0 ? (
                  Object.entries(
                    destinations.reduce((acc, dest) => {
                      const continent = dest.continent || "Otros";
                      if (!acc[continent]) acc[continent] = [];
                      acc[continent].push(dest);
                      return acc;
                    }, {})
                  ).map(([continent, items]) => (
                    <div key={continent}>
                      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 border-b border-slate-50 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-accent/50" />
                        {continent}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {items.map((dest) => (
                          <Link
                            key={dest.slug}
                            to={`/destinos/${dest.slug}`}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="text-sm text-muted-foreground hover:text-primary py-1"
                          >
                            {dest.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground italic">Cargando destinos...</p>
                )}
              </div>
            </div>

            <Link
              to="/viajes"
              onClick={() => setIsMobileMenuOpen(false)}
              className="mobile-menu-link"
            >
              Colección de Viajes
            </Link>

            <Link
              to="/guias-personalizadas"
              onClick={() => setIsMobileMenuOpen(false)}
              className="mobile-menu-link"
            >
              Guías Personalizadas
            </Link>

            <Link
              to="/servicios-empresas"
              onClick={() => setIsMobileMenuOpen(false)}
              className="mobile-menu-link"
            >
              Colectivos
            </Link>

            <Link
              to="/sobre-nosotros"
              onClick={() => setIsMobileMenuOpen(false)}
              className="mobile-menu-link"
            >
              Sobre Nosotros
            </Link>

            <Link
              to="/blog"
              onClick={() => setIsMobileMenuOpen(false)}
              className="mobile-menu-link"
            >
              Blog
            </Link>
          </div>

          <div className="mt-auto">
            <Link
              to="/contacto"
              onClick={() => setIsMobileMenuOpen(false)}
              className="btn-accent w-full text-center block"
            >
              Planifica tu Viaje
            </Link>
          </div>
        </div>
      )}
    </>
  );
}
